import { useState } from "react";
import { ListOrdered } from "lucide-react";

import Panel from "@/components/common/Panel";
import { cn } from "@/lib/utils";
import type { ManualPositionView } from "../../api/client";
import { ManualActionError, type ManualCockpitState } from "../../hooks/useManualCockpit";

/**
 * Positions ouvertes du cockpit MANUEL : clôture marché + avance de stop
 * (§19.5 — le backend refuse tout élargissement, l'erreur remonte en clair).
 */
export default function ManualPositionsPanel({ manual }: { manual: ManualCockpitState }) {
  const positions = manual.status?.positions ?? [];
  const armed = manual.status?.armed ?? false;
  const [stopDrafts, setStopDrafts] = useState<Record<string, string>>({});
  const [rowErr, setRowErr] = useState<Record<string, string>>({});

  const setDraft = (dealId: string, v: string) =>
    setStopDrafts((d) => ({ ...d, [dealId]: v }));

  const doClose = async (dealId: string) => {
    setRowErr((e) => ({ ...e, [dealId]: "" }));
    try {
      await manual.closePosition(dealId);
    } catch (e) {
      setRowErr((r) => ({
        ...r,
        [dealId]: e instanceof ManualActionError ? `${e.code} — ${e.detail}` : "Clôture refusée.",
      }));
    }
  };

  const doAdvance = async (p: ManualPositionView) => {
    const raw = stopDrafts[p.dealId];
    const target = Number(raw);
    if (!Number.isFinite(target) || raw === undefined || raw === "") return;
    setRowErr((e) => ({ ...e, [p.dealId]: "" }));
    try {
      await manual.advanceStop(p.dealId, target);
      setDraft(p.dealId, "");
    } catch (e) {
      setRowErr((r) => ({
        ...r,
        [p.dealId]:
          e instanceof ManualActionError ? `${e.code} — ${e.detail}` : "Avance de stop refusée.",
      }));
    }
  };

  return (
    <Panel
      title="Positions manuelles"
      icon={<ListOrdered className="size-4" aria-hidden />}
      aside={<span className="text-muted-foreground text-xs tabular-nums">{positions.length} ouverte(s)</span>}
      aria-label="Positions manuelles"
    >
      {positions.length === 0 ? (
        <p className="text-muted-foreground text-sm" data-testid="manual-positions-empty">
          Aucune position manuelle ouverte.
        </p>
      ) : (
        <ul className="flex flex-col gap-2" data-testid="manual-positions">
          {positions.map((p) => (
            <li
              key={p.dealId}
              className="border-border flex flex-col gap-2 rounded-lg border bg-card p-3"
              data-testid={`manual-position-${p.dealId}`}
            >
              <div className="flex items-center gap-2 text-sm">
                <span
                  className={cn(
                    "rounded px-1.5 py-0.5 text-xs font-bold",
                    p.direction === "BUY" ? "bg-buy/15 text-buy" : "bg-sell/15 text-sell",
                  )}
                >
                  {p.direction}
                </span>
                {p.origin === "RANGE" && (
                  <span
                    className="border-info/40 text-info rounded border px-1.5 py-0.5 text-xs font-medium"
                    title="Ouverte par le mode range — sera retournée au bord opposé"
                    data-testid={`manual-origin-range-${p.dealId}`}
                  >
                    range
                  </span>
                )}
                <span className="font-mono tabular-nums">{p.sizePerPointEur} €/pt</span>
                <span className="text-muted-foreground">
                  entrée <span className="text-foreground font-mono tabular-nums">{p.entryPrice}</span>
                </span>
                <span className="text-muted-foreground">
                  stop <span className="text-foreground font-mono tabular-nums">{p.stopPrice}</span>
                </span>
                <span className="text-muted-foreground ml-auto font-mono text-xs">{p.dealId}</span>
              </div>
              {armed && (
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    type="number"
                    step="1"
                    placeholder={`stop > ${p.stopPrice}`}
                    value={stopDrafts[p.dealId] ?? ""}
                    onChange={(e) => setDraft(p.dealId, e.target.value)}
                    aria-label={`Nouveau stop pour ${p.dealId}`}
                    className="bg-background border-border focus:border-info w-36 rounded-lg border px-2 py-1.5 font-mono text-xs outline-none"
                    data-testid={`manual-stop-input-${p.dealId}`}
                  />
                  <button
                    type="button"
                    onClick={() => void doAdvance(p)}
                    disabled={manual.pending}
                    className="border-info/40 text-info hover:bg-info/10 rounded-lg border px-3 py-1.5 text-xs font-medium disabled:opacity-50"
                    data-testid={`manual-advance-${p.dealId}`}
                  >
                    Avancer le stop
                  </button>
                  <button
                    type="button"
                    onClick={() => void doClose(p.dealId)}
                    disabled={manual.pending}
                    className="border-loss/40 text-loss hover:bg-loss/10 ml-auto rounded-lg border px-3 py-1.5 text-xs font-medium disabled:opacity-50"
                    data-testid={`manual-close-${p.dealId}`}
                  >
                    Clôturer
                  </button>
                </div>
              )}
              {rowErr[p.dealId] && (
                <p className="text-loss text-xs" role="alert">
                  {rowErr[p.dealId]}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}
